import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel from "../SkillControlsPanel";

export interface SapConfig {
  id: number;
  siteId: number;
  product: "BusinessOne" | "S4Hana";
  sapUrl: string;
  enabled: boolean;
  companyDb?: string;
  b1Username?: string;
  createdAt: string;
  updatedAt?: string;
}

const CAPABILITIES = [
  {
    title: "Business partner lookup",
    icon: "👤",
    items: [
      "Search SAP customers and suppliers by name, email, or phone number",
      "Retrieve full account details: balance, credit limit, currency, contact person",
      "Screen pop on inbound calls — instantly surfaces the caller's SAP record",
      "Supports both SAP Business One and S/4HANA (same skill set, auto-detects product)"
    ]
  },
  {
    title: "Orders & invoices",
    icon: "📦",
    items: [
      "Look up sales orders by order number or customer — returns line items, quantities, and pricing",
      "View open (unpaid) AR invoices with balance due and due date",
      "Retrieve specific invoices by doc entry number",
      "Create new sales orders from chat with item codes, quantities, and optional pricing overrides"
    ]
  },
  {
    title: "Inventory & items",
    icon: "🏭",
    items: [
      "Search the SAP item master by name or item code — returns pricing and availability flags",
      "Check real-time stock levels across all warehouses: on-hand, committed, ordered, available",
      "Ideal for manufacturing, distribution, and wholesale — instant availability answers without leaving chat"
    ]
  },
  {
    title: "Service calls",
    icon: "🔧",
    items: [
      "Create SAP service calls (support tickets) with subject, description, and priority from chat",
      "View all open service calls for a customer — status, priority, creation date",
      "Update service call status and add resolution notes",
      "Log chat activity notes directly to a service call record"
    ]
  },
  {
    title: "Quotations & deliveries",
    icon: "📋",
    items: [
      "Create and track sales quotations",
      "View delivery notes and fulfillment status",
      "Check return orders"
    ]
  },
  {
    title: "Purchasing & vendors",
    icon: "🛒",
    items: [
      "Search vendors and suppliers",
      "View open purchase orders",
      "Check AP invoices",
      "Get goods receipt confirmations"
    ]
  }
];

const AI_TOOLS = [
  { name: "sap_search_business_partners", label: "Search Business Partners", tier: "Read" },
  { name: "sap_get_business_partner", label: "Get Business Partner", tier: "Read" },
  { name: "sap_get_orders_by_partner", label: "Get Orders by Partner", tier: "Read" },
  { name: "sap_get_sales_order", label: "Get Sales Order", tier: "Read" },
  { name: "sap_search_sales_orders", label: "Search Sales Orders", tier: "Read" },
  { name: "sap_get_open_invoices", label: "Get Open Invoices", tier: "Read" },
  { name: "sap_get_invoice", label: "Get Invoice", tier: "Read" },
  { name: "sap_search_items", label: "Search Items", tier: "Read" },
  { name: "sap_get_item", label: "Get Item", tier: "Read" },
  { name: "sap_get_stock", label: "Get Stock Level", tier: "Read" },
  { name: "sap_get_service_calls", label: "Get Service Calls", tier: "Read" },
  { name: "sap_create_sales_order", label: "Create Sales Order", tier: "Write" },
  { name: "sap_create_service_call", label: "Create Service Call", tier: "Write" },
  { name: "sap_update_service_call", label: "Update Service Call", tier: "Write" },
  { name: "sap_add_service_call_note", label: "Add Service Call Note", tier: "Write" },
  // Quotations
  { name: "sap_get_quotations", label: "Get Quotations", tier: "Read" },
  { name: "sap_get_quotation", label: "Get Quotation", tier: "Read" },
  { name: "sap_create_quotation", label: "Create Quotation", tier: "Write" },
  // Deliveries
  { name: "sap_get_deliveries", label: "Get Deliveries", tier: "Read" },
  { name: "sap_get_delivery", label: "Get Delivery Note", tier: "Read" },
  // Payments / AR
  { name: "sap_get_incoming_payments", label: "Get Incoming Payments", tier: "Read" },
  { name: "sap_get_credit_notes", label: "Get Credit Notes", tier: "Read" },
  // Purchasing / AP
  { name: "sap_search_vendors", label: "Search Vendors", tier: "Read" },
  { name: "sap_get_purchase_orders", label: "Get Purchase Orders", tier: "Read" },
  { name: "sap_get_ap_invoices", label: "Get AP Invoices", tier: "Read" },
  // Contacts
  { name: "sap_get_contact_persons", label: "Get Contact Persons", tier: "Read" },
  { name: "sap_add_contact_person", label: "Add Contact Person", tier: "Write" },
  // CRM Activities
  { name: "sap_get_activities", label: "Get Activities", tier: "Read" },
  { name: "sap_create_activity", label: "Create Activity", tier: "Write" },
  // Production (manufacturing)
  { name: "sap_get_production_orders", label: "Get Production Orders", tier: "Read" },
  { name: "sap_get_production_order", label: "Get Production Order", tier: "Read" },
  // Returns / Goods
  { name: "sap_get_returns", label: "Get Returns", tier: "Read" },
  { name: "sap_get_goods_receipts", label: "Get Goods Receipts", tier: "Read" }
];

export default function SAP() {
  const [config, setConfig] = useState<SapConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"overview" | "skills" | "log">("overview");
  const navigate = useNavigate();
  const api = useApi();

  const load = useCallback(async () => {
    try {
      const resp = await api.messaging.get("Sap/config");
      if (resp.ok) {
        const data = await resp.json();
        setConfig(Array.isArray(data) ? data[0] ?? null : data);
      } else {
        setConfig(null);
      }
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;

  if (!config) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">SAP Business One</h1>
          <p className="text-gray-500 mt-1">
            Connect SAP to search customers, orders, invoices, inventory, and service calls — all from live chat or IVR.
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">SAP is not connected yet.</p>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            onClick={() => navigate("/integrations/sap/create")}
          >
            Connect SAP
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {CAPABILITIES.map((cap) => (
            <div key={cap.title} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{cap.icon}</span>
                <h3 className="font-semibold text-gray-900">{cap.title}</h3>
              </div>
              <ul className="space-y-1">
                {cap.items.map((item) => (
                  <li key={item} className="text-sm text-gray-600 flex gap-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SAP Business One</h1>
          <p className="text-sm text-gray-500 mt-1">
            {config.sapUrl} {config.companyDb ? `· ${config.companyDb}` : ""}
            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
              {config.enabled ? "Connected" : "Disabled"}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
            <button
            className="text-gray-400 hover:text-gray-600"
            onClick={() => navigate("/integrations/sap/create")}
            title="Edit connection"
          >
            <IconClose1 />
          </button>
        </div>
      </div>

      <div className="flex gap-4 border-b border-gray-200 mb-6">
        {(["overview", "skills", "log"] as const).map((tab) => (
          <button
            key={tab}
            className={`pb-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${activeTab === tab ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "log" ? "Activity Log" : tab === "skills" ? "AI Tools" : "Overview"}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CAPABILITIES.map((cap) => (
            <div key={cap.title} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{cap.icon}</span>
                <h3 className="font-semibold text-gray-900">{cap.title}</h3>
              </div>
              <ul className="space-y-1">
                {cap.items.map((item) => (
                  <li key={item} className="text-sm text-gray-600 flex gap-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {activeTab === "skills" && (
        <SkillControlsPanel
          integration="SAP"
          skills={AI_TOOLS.map(t => ({ name: t.name, label: t.label, description: t.tier === "Write" ? "Creates or updates records in SAP. Requires Pro tier." : "Reads data from SAP.", write: t.tier === "Write" }))}
        />
      )}

      {activeTab === "log" && (
        <IntegrationActivityLog integrationSlug="SAP" />
      )}
    </div>
  );
}
